import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Download, Briefcase } from 'lucide-react';
import SEO from '../../components/SEO';
import SeoNav from '../../components/SeoNav';

const schema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'What should a consultant invoice include?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'A consultant invoice should include your name or firm name and contact details, the client name and engagement reference, a unique invoice number and date, payment due date, a description of consulting services rendered, your daily or hourly rate and the time spent, any travel or out-of-pocket expenses, applicable tax (VAT or GST), and the total amount due with your bank details.',
      },
    },
    {
      '@type': 'Question',
      name: 'How do consultants typically structure their invoices?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Most consultants invoice either per day rate (common in management consulting, IT consulting) or per hour. Some use fixed project fees for defined deliverables. A typical consultant invoice lists the number of days or hours, the agreed rate, a brief description of the work period or project phase, and then any expenses separately. Always reference the consulting agreement or purchase order number if one exists.',
      },
    },
    {
      '@type': 'Question',
      name: 'Should consultants charge VAT or GST?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes, if your annual turnover exceeds the registration threshold in your country. In the UK, VAT applies at 20% once turnover exceeds £90,000. In India, GST at 18% applies to consulting services for turnover over ₹20 lakh. In Australia, add 10% GST if your turnover exceeds AUD $75,000. US consultants generally do not charge sales tax on services, though rules vary by state.',
      },
    },
    {
      '@type': 'Question',
      name: 'Can I invoice consulting expenses separately?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes, and it is best practice to do so. List consulting fees and reimbursable expenses as separate line items. Typical expenses include travel (flights, accommodation, mileage), subsistence, research tools, or printing. Attach receipts or an expense report as a supporting document. Make sure your consulting agreement covers expense reimbursement terms before you incur the costs.',
      },
    },
    {
      '@type': 'Question',
      name: 'Is this consultant invoice template free?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. Invilic provides a free consultant invoice template. Fill in your engagement details online and download a professional PDF instantly. Free for up to 5 invoices per month with no sign-up required.',
      },
    },
  ],
};

export default function ConsultantInvoiceTemplatePage() {
  return (
    <div className="min-h-screen bg-white dark:bg-gray-950">
      <SEO
        title="Consultant Invoice Template: Free PDF for Consultants | Invilic"
        description="Free consultant invoice template. Bill for consulting days, hourly work, and reimbursable expenses. Download a professional PDF instantly. No sign-up needed."
        canonical="https://www.invilic.com/consultant-invoice-template"
        schema={schema}
      />

      <SeoNav />

      <main className="max-w-4xl mx-auto px-6 py-16">
        <div className="text-center mb-16">
          <span className="inline-block bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 text-xs font-semibold px-3 py-1 rounded-full uppercase tracking-wider mb-4">
            Free Template
          </span>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-gray-900 dark:text-white leading-tight mb-5">
            Consultant Invoice Template
          </h1>
          <p className="text-xl text-gray-500 dark:text-gray-400 max-w-2xl mx-auto leading-relaxed mb-8">
            A professional invoice template for independent consultants and advisory firms. Bill by the day, hour, or project. Download a clean PDF in seconds — no account needed.
          </p>
          <Link
            to="/create/freelancer"
            className="inline-flex items-center gap-2 bg-amber-600 hover:bg-amber-700 text-white font-semibold px-8 py-4 rounded-xl shadow-lg shadow-amber-600/20 transition-all duration-200 text-lg"
          >
            Use Free Template <ArrowRight className="w-5 h-5" />
          </Link>
          <p className="text-sm text-gray-400 dark:text-gray-500 mt-3">Free · No sign-up · PDF in seconds</p>
        </div>

        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8 text-center">
            Built for Every Type of Consultant
          </h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              { title: 'Management consultants', desc: 'Bill clients for strategy engagements, process audits, and advisory days with clear day rates.' },
              { title: 'IT & technology consultants', desc: 'Invoice for architecture reviews, system implementations, and technical advisory work.' },
              { title: 'HR & organisational consultants', desc: 'Bill for talent strategy, training programmes, and change management engagements.' },
              { title: 'Financial & business consultants', desc: 'Issue professional invoices for financial modelling, business planning, and due diligence.' },
            ].map(({ title, desc }) => (
              <div key={title} className="flex gap-4 p-5 rounded-xl border border-gray-100 dark:border-gray-800">
                <div className="w-10 h-10 rounded-xl bg-amber-100 dark:bg-amber-900/40 flex items-center justify-center flex-shrink-0">
                  <Briefcase className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                </div>
                <div>
                  <div className="font-semibold text-gray-900 dark:text-white text-sm mb-1">{title}</div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8 text-center">
            What's in the Consultant Invoice Template
          </h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {[
              'Consultant / firm name and logo',
              'Client name and engagement reference',
              'Invoice number and date',
              'Payment due date',
              'Service description per line item',
              'Days worked and day rate (or hours)',
              'Travel and out-of-pocket expenses',
              'PO number or contract reference',
              'Subtotal, tax (VAT/GST) and discount',
              'Total amount due',
              'Bank / wire transfer details',
              'Payment terms and notes',
            ].map(f => (
              <div key={f} className="flex items-center gap-3 p-4 rounded-xl border border-amber-100 dark:border-amber-800/30 bg-amber-50 dark:bg-amber-900/10">
                <CheckCircle className="w-4 h-4 text-amber-600 dark:text-amber-400 flex-shrink-0" />
                <span className="text-sm text-gray-700 dark:text-gray-300">{f}</span>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8 text-center">
            How to Create Your Consulting Invoice
          </h2>
          <div className="grid sm:grid-cols-3 gap-6">
            {[
              { n: '1', title: 'Open the template', desc: 'Click "Use Free Template" above. No software to install, no account needed.' },
              { n: '2', title: 'Add your engagement details', desc: 'Enter your work, days or hours, rate, and any expenses.' },
              { n: '3', title: 'Download PDF', desc: 'Download a professional PDF invoice ready to send to your client.' },
            ].map(({ n, title, desc }) => (
              <div key={n} className="text-center p-6 rounded-2xl border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/50">
                <div className="w-10 h-10 rounded-full bg-amber-600 text-white font-bold text-lg flex items-center justify-center mx-auto mb-4">
                  {n}
                </div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">{title}</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">{desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8">
            Consultant Invoice Template: FAQs
          </h2>
          <div className="space-y-4">
            {schema.mainEntity.map(({ name, acceptedAnswer }) => (
              <details key={name} className="group border border-gray-100 dark:border-gray-800 rounded-xl overflow-hidden">
                <summary className="flex items-center justify-between px-6 py-4 cursor-pointer font-medium text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-900/50 list-none">
                  {name}
                  <ArrowRight className="w-4 h-4 text-gray-400 transition-transform group-open:rotate-90 flex-shrink-0 ml-4" />
                </summary>
                <p className="px-6 pb-5 text-gray-500 dark:text-gray-400 text-sm leading-relaxed">
                  {acceptedAnswer.text}
                </p>
              </details>
            ))}
          </div>
        </section>

        <section className="text-center py-12 border-t border-gray-100 dark:border-gray-800">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
            Create Your Consulting Invoice Now for Free
          </h2>
          <p className="text-gray-500 dark:text-gray-400 mb-8">Professional PDF. No account required. Ready in 2 minutes.</p>
          <Link
            to="/create/freelancer"
            className="inline-flex items-center gap-2 bg-amber-600 hover:bg-amber-700 text-white font-semibold px-8 py-4 rounded-xl shadow-lg shadow-amber-600/20 transition-all duration-200 text-lg"
          >
            <Download className="w-5 h-5" /> Get Free Consultant Invoice Template
          </Link>
        </section>
      </main>
    </div>
  );
}
